import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { useOrgGuestFeedbackCsvUploadsSidebarQuery } from "./GuestFeedbackCsvUploadDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import FileOutlineIcon from "@src/lib/icons/18px/file-outline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { cn } from "@src/lib/utils/cn";
import { MapPinIcon } from "lucide-react";
import { format } from "date-fns";
import { csvUploadFileName } from "./csvUploadFileName";

const StarRating: React.FC<{ rating: number | null | undefined }> = ({
  rating,
}) => {
  if (rating == null)
    return (
      <Text variant="fine-print-description" className="text-muted-foreground">
        —
      </Text>
    );
  const rounded = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "text-sm",
            i < rounded ? "text-yellow-7" : "text-gray-3",
          )}
        >
          ★
        </span>
      ))}
      <Text
        variant="fine-print-description"
        className="ml-1 text-muted-foreground"
      >
        {rating.toFixed(1)}
      </Text>
    </div>
  );
};

const PAGE_SIZE = 25;

type Props = {
  rowId: string;
  showHeader?: boolean;
};

gql`
  query OrgGuestFeedbackCsvUploadsSidebar {
    OrgGuestFeedbackCsvUploads {
      objects {
        id
        fileUrl
        fileName
        feedbackDateColumn
        feedbackTextColumn
        ratingColumn
        locationColumn
        feedbacks {
          id
          text
          rating
          date
          csvUploadLocation {
            id
            locationName
          }
        }
        locations {
          id
          locationName
          location {
            id
            name
          }
        }
      }
    }
  }
`;

const GuestFeedbackCsvUploadDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const [feedbackPage, setFeedbackPage] = useState(0);

  const { data } = useOrgGuestFeedbackCsvUploadsSidebarQuery({
    fetchPolicy: "cache-first",
    skip: !rowId,
  });

  const upload = useMemo(() => {
    return (
      data?.OrgGuestFeedbackCsvUploads?.objects?.find((u) => u.id === rowId) ??
      null
    );
  }, [data, rowId]);

  if (!upload) return null;

  const feedbacksCount = upload.feedbacks?.length ?? 0;
  const locationsCount = upload.locations?.length ?? 0;
  const totalPages = Math.ceil(feedbacksCount / PAGE_SIZE);
  const paginatedFeedbacks =
    upload.feedbacks?.slice(
      feedbackPage * PAGE_SIZE,
      (feedbackPage + 1) * PAGE_SIZE,
    ) ?? [];

  const detailsItems: InfoTableItem[] = [
    {
      label: "Upload ID",
      value: upload.id,
    },
    {
      label: "File",
      value: (
        <a
          href={upload.fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          <Text variant="compact-description">{csvUploadFileName(upload)}</Text>
        </a>
      ),
    },
    {
      label: "Feedback Date Column",
      value: upload.feedbackDateColumn || "--",
    },
    {
      label: "Feedback Text Column",
      value: upload.feedbackTextColumn || "--",
    },
    {
      label: "Rating Column",
      value: upload.ratingColumn || "--",
    },
    {
      label: "Location Column",
      value: upload.locationColumn || "--",
    },
    {
      label: "Reviews",
      value: String(feedbacksCount),
    },
    {
      label: "Locations",
      value: String(locationsCount),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title="CSV Upload"
        icon={<FileOutlineIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.GuestFeedbackCsvUpload}
      />
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <Tabs size="sm" defaultValue="details">
            <div className="flex border-b border-border-primary">
              <TabsList fill aria-label="CSV upload">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger
                  value="locations"
                  count={locationsCount || undefined}
                >
                  Locations
                </TabsTrigger>
                <TabsTrigger
                  value="feedbacks"
                  count={feedbacksCount || undefined}
                >
                  Reviews
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="details" className="mt-3">
              <InfoTable items={detailsItems} />
            </TabsContent>
            <TabsContent value="locations" className="mt-3">
              {!upload.locations || upload.locations.length === 0 ? (
                <div className="p-4">
                  <div className="text-gray-7">No locations in this upload</div>
                </div>
              ) : (
                <div className="p-4">
                  <HtmlTable className="w-full">
                    <HtmlTableHeader>
                      <HtmlTableRow>
                        <HtmlTableHead>CSV Location</HtmlTableHead>
                        <HtmlTableHead>Opus Location</HtmlTableHead>
                      </HtmlTableRow>
                    </HtmlTableHeader>
                    <HtmlTableBody>
                      {upload.locations.map((loc) => (
                        <HtmlTableRow key={loc.id}>
                          <HtmlTableCell>
                            <div className="flex items-center gap-2">
                              <MapPinIcon className="size-4 text-gray-7" />
                              <Text variant="compact-title">
                                {loc.locationName}
                              </Text>
                            </div>
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <Text
                              variant="compact-description"
                              className="text-muted-foreground"
                            >
                              {loc.location?.name || "--"}
                            </Text>
                          </HtmlTableCell>
                        </HtmlTableRow>
                      ))}
                    </HtmlTableBody>
                  </HtmlTable>
                </div>
              )}
            </TabsContent>
            <TabsContent value="feedbacks" className="mt-3">
              {!upload.feedbacks || upload.feedbacks.length === 0 ? (
                <div className="p-4">
                  <div className="text-gray-7">No reviews in this upload</div>
                </div>
              ) : (
                <div className="p-4">
                  <HtmlTable>
                    <HtmlTableHeader>
                      <HtmlTableRow>
                        <HtmlTableHead className="w-[100px]">
                          Date
                        </HtmlTableHead>
                        <HtmlTableHead className="w-[120px]">
                          Rating
                        </HtmlTableHead>
                        <HtmlTableHead className="w-[140px]">
                          Location
                        </HtmlTableHead>
                        <HtmlTableHead>Review</HtmlTableHead>
                      </HtmlTableRow>
                    </HtmlTableHeader>
                    <HtmlTableBody>
                      {paginatedFeedbacks.map((feedback) => (
                        <HtmlTableRow key={feedback.id}>
                          <HtmlTableCell>
                            <Text
                              variant="fine-print-description"
                              className="text-muted-foreground"
                            >
                              {feedback.date
                                ? format(new Date(feedback.date), "MMM d, yyyy")
                                : "—"}
                            </Text>
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <StarRating rating={feedback.rating} />
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <Text
                              variant="fine-print-description"
                              className="text-muted-foreground"
                            >
                              {feedback.csvUploadLocation?.locationName ?? "—"}
                            </Text>
                          </HtmlTableCell>
                          <HtmlTableCell>
                            <Text
                              variant="compact-description"
                              className="line-clamp-2"
                            >
                              {feedback.text ?? "—"}
                            </Text>
                          </HtmlTableCell>
                        </HtmlTableRow>
                      ))}
                    </HtmlTableBody>
                  </HtmlTable>
                  {totalPages > 1 && (
                    <div className="mt-3 flex items-center justify-between">
                      <Text
                        variant="fine-print-description"
                        className="text-muted-foreground"
                      >
                        Page {feedbackPage + 1} of {totalPages}
                      </Text>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={feedbackPage === 0}
                          onClick={() => setFeedbackPage((p) => p - 1)}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={feedbackPage >= totalPages - 1}
                          onClick={() => setFeedbackPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SidebarContent>
    </div>
  );
};

export default GuestFeedbackCsvUploadDetailSidebar;
